/**
 * As classes do botão, separadas do componente.
 *
 * Vive em arquivo próprio por uma razão mecânica, a mesma de
 * `contexto-sessao.ts`: um módulo que exporta componente E não-componente
 * quebra o fast refresh do Vite — cada edição remonta a árvore inteira e o
 * formulário que estava preenchido se perde. O eslint reprova isso
 * (`react-refresh/only-export-components`), e o CI roda com
 * `--max-warnings 0`.
 *
 * Quem quer o BOTÃO importa `Botao`. Quem precisa só da aparência — o
 * `<label>` que dispara o seletor de arquivo, o `<a>` que sai do app — importa
 * `classesDeBotao` daqui.
 */
/**
 * A intenção do botão, nunca a cor dele.
 *
 * `escuro` é o CTA de resumo da spec 01 — "finalizar venda", "lançar" — que vai
 * em `tinta` e não em azul: no PDV o azul já é a cor da seleção, e um botão
 * azul no rodapé competiria com a linha selecionada da lista.
 */
export type VarianteBotao = 'primario' | 'secundario' | 'escuro' | 'perigo' | 'sucesso'

/**
 * A altura, pelo PAPEL do botão — não pelo número.
 *
 * Os quatro primeiros são a tabela "Controles" da spec 01. `auth` é o campo
 * alto das telas de acesso, que seguem outra escala porque são a única parte do
 * produto vista de longe, sem contexto e por quem ainda não é usuário.
 */
export type PorteBotao = 'padrao' | 'medio' | 'destaque' | 'cta' | 'auth'

/*
  A altura vai como CLASSE, e não como `style`, porque metade dos "botões" do
  sistema não é `<button>`: "Novo produto" é um `<Link>`, "Escolher arquivo" é
  um `<label>`. Com a altura presa ao componente React, esses casos ficavam de
  fora e copiavam tudo à mão.

  `altura` continua existindo como prop para os dois valores do handoff que não
  estão na tabela (54px no login, 50px no wizard) — e aí sim vai por `style`,
  que vence a classe.
*/
const ALTURA: Record<PorteBotao, string> = {
  padrao: 'h-[34px]',
  medio: 'h-[40px]',
  destaque: 'h-[42px]',
  cta: 'h-[46px]',
  auth: 'h-[52px]',
}

/* O raio acompanha o porte: a spec usa 8px no sistema e 10px nas telas de
   acesso. É a mesma regra que `Campo` já segue, e por isso não vira decisão. */
const RAIO: Record<PorteBotao, string> = {
  padrao: 'rounded-sistema',
  medio: 'rounded-sistema',
  destaque: 'rounded-sistema',
  cta: 'rounded-sistema',
  auth: 'rounded-auth',
}

const TIPOGRAFIA: Record<PorteBotao, string> = {
  padrao: 'text-[13.5px] font-semibold',
  medio: 'text-sm font-bold',
  destaque: 'text-sm font-bold',
  cta: 'text-sm font-bold',
  auth: 'text-[15px] font-bold',
}

const RECUO: Record<PorteBotao, string> = {
  padrao: 'px-4',
  medio: 'px-5',
  destaque: 'px-5',
  cta: 'px-5',
  auth: 'px-6',
}

/*
  Cada variante declara os TRÊS estados de cor de uma vez — padrão, hover e
  desabilitado. Estão na mesma string de propósito: separá-los em três mapas é
  como um deles fica sem entrada e ninguém percebe, que é a história dos 14
  botões sem hover.

  O desabilitado é o mesmo em todas: `bg-inativo`. Antes havia `bg-texto-fraco`
  em algumas e nada em outras — três respostas para "este botão não está
  disponível", quando a pergunta é só uma.
*/
const VARIANTE: Record<VarianteBotao, string> = {
  primario: 'bg-marca text-white hover:bg-marca-forte disabled:bg-inativo disabled:text-white',
  secundario:
    'border border-borda bg-superficie text-texto hover:border-inativo hover:bg-divisoria disabled:border-borda disabled:bg-divisoria disabled:text-texto-fraco',
  escuro: 'bg-tinta text-white hover:bg-titulo disabled:bg-inativo disabled:text-white',
  perigo: 'bg-erro text-white hover:bg-erro-texto disabled:bg-inativo disabled:text-white',
  sucesso: 'bg-ok text-white hover:bg-ok-texto disabled:bg-inativo disabled:text-white',
}

/**
 * As classes do botão, sem o botão.
 *
 * Existe exportada porque há um punhado de lugares que PRECISAM de outro
 * elemento com aparência de botão e não podem ser um `<button>`: o `<label>`
 * que dispara o seletor de arquivo da importação de nota fiscal é o caso
 * clássico — um `<button>` ali não abriria o seletor.
 *
 * Sem esta função, esses casos copiavam as classes à mão, e a cópia é o começo
 * do desvio. Com ela, continuam saindo da mesma fonte de verdade.
 */
export function classesDeBotao({
  variante = 'primario',
  porte = 'padrao',
  larguraTotal = false,
}: {
  variante?: VarianteBotao
  porte?: PorteBotao
  larguraTotal?: boolean
} = {}) {
  return [
    // `shrink-0` e `whitespace-nowrap`: um botão curto ao lado de um irmão
    // `flex-1` era espremido até o rótulo quebrar em duas linhas — aconteceu
    // com "Voltar" e "Adicionar".
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-corpo transition-colors duration-150',
    ALTURA[porte],
    RAIO[porte],
    RECUO[porte],
    TIPOGRAFIA[porte],
    VARIANTE[variante],
    larguraTotal ? 'w-full' : '',
  ]
    .filter(Boolean)
    .join(' ')
}
